import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../LandingPage.css";
import Button from "../ui/Button";
import InputField from "../ui/InputField";
import SimpleCard from "../ui/SimpleCardStart";
import {
    ChevronsDown,
    Facebook,
    Instagram,
    Linkedin,
    MessageCircle,
    Construction,
    Trees,
    Contact
} from "lucide-react";
import FeatureCard from "../ui/FeatureCardStart";
import Textarea from "../ui/Textarea";
import logo from "../log.svg";

export default function LandingPageStart() {
    localStorage.setItem("page", " ");



    const navigate = useNavigate();
    const scrollRef = useRef(null);
    const [activeSection, setActiveSection] = useState("section_intro");
    const isAnimatingRef = useRef(false);

    const scrollToNextSection = () => {
        const sections = Array.from(
            scrollRef.current.querySelectorAll(".section")
        );
        const currentIndex = sections.findIndex(s => s.id === activeSection);
        const next = sections[currentIndex + 1];
        if (next) next.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const showCopiedTooltip = (event) => {
        const el = event.currentTarget;
        const previousTitle = el.getAttribute("title");

        el.setAttribute("title", "Kopiert");

        setTimeout(() => {
            if (previousTitle) {
                el.setAttribute("title", previousTitle);
            } else {
                el.removeAttribute("title");
            }
        }, 1200);
    };

    // Oben im Component State hinzufügen
    const [formData, setFormData] = useState({ Name: "", Email: "", Firma: "", Nachricht: "" });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        // Kurzer Check, ob alles gefüllt ist
        if (!formData.Name || !formData.Email || !formData.Nachricht) {
            alert("Bitte fülle die Pflichtfelder aus.");
            return;
        }

        try {
            const response = await fetch("./send_mail.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData)
            });

            const result = await response.json(); // Antwort vom Server lesen
            console.log("Server Antwort:", result);

            if (response.ok) {
                alert("Nachricht erfolgreich gesendet!");
            } else {
                alert("Server meldet: " + result.message);
            }
        } catch (err) {
            console.error("Fetch Fehler:", err);
            alert("Technischer Fehler beim Verbindungsaufbau.");
        }
    };

    const handleUrlClick = (platform) => () => {
        // Definition der Ziel-URLs
        const urls = {
            Facebook: "https://www.facebook.com/profile.php?id=61573824285052&locale=de_DE",
            Instagram: "https://www.instagram.com/log.stock/",
            LinkedIn: "https://www.linkedin.com/company/log-stock/posts/?feedView=all"
        };

        const targetUrl = urls[platform];

        if (targetUrl) {
            window.open(targetUrl, "_blank", "noopener,noreferrer");
        } else {
            console.warn(`Keine URL für ${platform} gefunden.`);
        }
    };

    const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    /* ===== Intersection Observer ===== */
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        const sections = Array.from(container.querySelectorAll(".section"));

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            { root: container, threshold: 0.6 }
        );

        sections.forEach(section => observer.observe(section));
        return () => observer.disconnect();
    }, []);

    /* ===== Wheel Snap Scroll ===== */
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        const sections = Array.from(container.querySelectorAll(".section"));
        let accumulatedDelta = 0;

        const onWheel = (e) => {
            // Wenn es ein Touch-Gerät ist, Snap-Logik ignorieren
            if (window.innerWidth <= 768) return;

            e.preventDefault();
            if (isAnimatingRef.current) return;

            accumulatedDelta += e.deltaY;
            const THRESHOLD = 120;
            if (Math.abs(accumulatedDelta) < THRESHOLD) return;

            const direction = accumulatedDelta > 0 ? 1 : -1;
            accumulatedDelta = 0;

            const currentIndex = sections.findIndex(s => s.id === activeSection);
            const nextIndex = currentIndex + direction;
            if (nextIndex < 0 || nextIndex >= sections.length) return;

            isAnimatingRef.current = true;
            sections[nextIndex].scrollIntoView({ behavior: "smooth", block: "start" });

            setTimeout(() => {
                isAnimatingRef.current = false;
            }, 800);
        };

        container.addEventListener("wheel", onWheel, { passive: false });
        return () => container.removeEventListener("wheel", onWheel);
    }, [activeSection]);

    return (
        <div className="unset_margin page-layout">

            {/* ===== Scroll Indicator ===== */}
            <div className="scroll-indicator">
                {(isMobile ? [
                    "section_intro",
                    "section_welcome_1",
                    "section_welcome_2",
                    "section_welcome_3",
                    "section_contact"
                ] : [
                    "section_intro",
                    "section_welcome",
                    "section_contact"
                ]).map(id => (
                    <div
                        key={id}
                        className={`dot ${activeSection === id ? "active" : ""}`}
                        onClick={() =>
                            document.getElementById(id)?.scrollIntoView({
                                behavior: "smooth",
                                block: "start"
                            })
                        }
                    />
                ))}
            </div>

            {activeSection !== "section_contact" && (
                <div
                    className={`scroll-next ${activeSection === "section_contact" ? "is-hidden" : ""}`}
                    onClick={scrollToNextSection}
                >
                    <ChevronsDown className="chevrons" size={42} />
                </div>
            )}

            <div
                ref={scrollRef}
                className="column column-padding-narrow scroll-overlay"
            >
                <div id="section_intro" className="section">
                    <div >
                        {isMobile ? (
                            <>
                                <div className="margin-top_8">
                                    <img id="lp_logo" src={logo} alt="Logo" className="navbar-logo" />
                                    <div className="landingPage_header fontInAccentColor" >Log-Stock</div>
                                    <div className="landingPage_header">Die Rundholzplattform</div>
                                    <div className="landingPage_header">Einfach effizient</div>

                                </div>
                                <h3 className="margin_top_large">
                                    Als Plattform bringen wir Rundholzspediteure,
                                    Holzhändler, Säge- und Holzverarbeitende Industrie zusammen für effiziente,
                                    transparente und vertrauensvolle Holzlogistik.
                                </h3>
                                <div className="margin_top_large inline margin-top_20">

                                </div>
                            </>
                        ) : (
                            <>
                                <div className="margin-top_8">
                                    <img id="lp_logo" src={logo} alt="Logo" className="navbar-logo" />
                                    <div className="landingPage_header fontInAccentColor" >Log-Stock</div>
                                    <div className="landingPage_header">Die Rundholzplattform.</div>
                                    <div className="landingPage_header">Einfach effizient.</div>

                                </div>
                                <h3 className="margin_top_large">
                                    Als Plattform bringen wir Rundholzspediteure,
                                    Holzhändler, Säge- und Holzverarbeitende Industrie zusammen – für effiziente,
                                    transparente und vertrauensvolle Holzlogistik.
                                </h3>
                                <div className="margin_top_large inline margin-top_20">

                                </div>
                            </>)}
                    </div>
                </div>

                {isMobile ? (
                    /* MOBILE VERSION: Drei separate Sektionen für Snap-Scroll */
                    <>
                        <div id="section_welcome_1" className="section">
                            <div className="simple_card_container">
                                <SimpleCard
                                    icon={<Construction size={64} color="var(--accent)" />}
                                    title="Die Seite befindet sich im Aufbau"
                                    description="Unsere Rundholzplattform steckt aktuell noch im Aufbau. Wir entwickeln die Plattform Schritt für Schritt weiter und sprechen bereits mit beteiligten Akteuren aus der Branche. Deren Meinung und Praxiserfahrung sind für uns extrem wichtig – wir wollen etwas schaffen, das wirklich funktioniert."
                                    className="temp_simple_card fontSize2_1rem"
                                />
                            </div>
                        </div>
                        <div id="section_welcome_2" className="section">
                            <div className="simple_card_container">
                                <SimpleCard
                                    icon={<Trees size={64} color="var(--accent)" />}
                                    title="Was dich hier erwartet"
                                    description="Freu dich auf eine moderne Plattform für Rundholztransporte übersichtlich, effizient und ohne unnötigen Papierkram. Angebot und Nachfrage sollen hier schnell und unkompliziert zusammenfinden. Kurz gesagt: weniger Aufwand, mehr Bewegung im Holz."
                                    className="temp_simple_card fontSize2_1rem"
                                />
                            </div>
                        </div>
                        <div id="section_welcome_3" className="section">
                            <div className="simple_card_container">
                                <SimpleCard
                                    icon={<Contact size={64} color="var(--accent)" />}
                                    title="Jetzt Kontakt aufnehmen"
                                    description="Du hast Fragen, Ideen oder willst von Anfang an dabei sein? Perfekt genau dafür sind wir offen. Schreib uns einfach, wir freuen uns über jeden Austausch und melden uns zeitnah zurück."
                                    className="temp_simple_card fontSize2_1rem"
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    <div id="section_welcome" className="section">
                        <div className="simple_card_container">
                            <SimpleCard
                                icon={<Construction size={64} color="var(--accent)" />}
                                title="Die Seite befindet sich im Aufbau"
                                description="Unsere Rundholzplattform steckt aktuell noch im Aufbau. Wir entwickeln die Plattform Schritt für Schritt weiter und sprechen bereits mit beteiligten Akteuren aus der Branche. Deren Meinung und Praxiserfahrung sind für uns extrem wichtig – wir wollen etwas schaffen, das wirklich funktioniert."
                                className="temp_simple_card fontSize2_1rem"
                            />
                            <SimpleCard
                                icon={<Trees size={64} color="var(--accent)" />}
                                title="Was dich hier erwartet"
                                description="Freu dich auf eine moderne Plattform für Rundholztransporte übersichtlich, effizient und ohne unnötigen Papierkram. Angebot und Nachfrage sollen hier schnell und unkompliziert zusammenfinden. Kurz gesagt: weniger Aufwand, mehr Bewegung im Holz."
                                className="temp_simple_card fontSize2_1rem"
                            />
                            <SimpleCard
                                icon={<Contact size={64} color="var(--accent)" />}
                                title="Jetzt Kontakt aufnehmen"
                                description="Du hast Fragen, Ideen oder willst von Anfang an dabei sein? Perfekt genau dafür sind wir offen. Schreib uns einfach, wir freuen uns über jeden Austausch und melden uns zeitnah zurück."
                                className="temp_simple_card fontSize2_1rem"
                            />
                        </div>
                    </div>
                )}

                <div id="section_contact" className="section">
                    {/* Der Haupt-Container mit Flexbox */}
                    <div className="contact-flex">

                        {/* Linke Spalte: Social Media */}
                        <div className="social-column">
                            {/* <h2>
                                Folg uns gerne auf...
                            </h2> */}
                            {isMobile ? (
                                <>
                                    <FeatureCard
                                        icon={<Facebook size={32} color="var(--accent)" />}
                                        // title="Facebook"
                                        className="center_content pointer backgroundBox_cornered"
                                        onClick={handleUrlClick("Facebook")}
                                    />

                                    <FeatureCard
                                        icon={<Instagram size={32} color="var(--accent)" />}
                                        // title="Instagram"
                                        className="center_content pointer backgroundBox_cornered"
                                        onClick={handleUrlClick("Instagram")}
                                    />

                                    <FeatureCard
                                        icon={<Linkedin size={32} color="var(--accent)" />}
                                        // title="LinkedIn"
                                        className="center_content pointer backgroundBox_cornered"
                                        onClick={handleUrlClick("LinkedIn")}
                                    />
                                    {/* <FeatureCard
                                        icon={<Linkedin size={32} color="var(--accent)" />}
                                        // title="LinkedIn"
                                        className="center_content pointer backgroundBox_cornered"
                                        onClick={handleUrlClick("LinkedIn")}
                                    /> */}
                                </>
                            ) : (
                                <>
                                    <FeatureCard
                                        icon={<Facebook size={32} color="var(--accent)" />}
                                        title="Facebook"
                                        className="center_content pointer backgroundBox_cornered"
                                        onClick={handleUrlClick("Facebook")}
                                    />

                                    <FeatureCard
                                        icon={<Instagram size={32} color="var(--accent)" />}
                                        title="Instagram"
                                        className="center_content pointer backgroundBox_cornered"
                                        onClick={handleUrlClick("Instagram")}
                                    />

                                    <FeatureCard
                                        icon={<Linkedin size={32} color="var(--accent)" />}
                                        title="LinkedIn"
                                        className="center_content pointer backgroundBox_cornered"
                                        onClick={handleUrlClick("LinkedIn")}
                                    />
                                </>
                            )}
                        </div>

                        {/* Rechte Spalte: Kontaktformular */}
                        <div className="form-column">
                            <h2>Sende uns eine Nachricht</h2>
                            <InputField label="Name" name="Name" placeholder="Name" value={formData.Name} onChange={handleInputChange} />
                            <InputField label="E-Mail" name="Email" placeholder="Email" value={formData.Email} onChange={handleInputChange} />
                            <InputField label="Firma" name="Firma" placeholder="Firma" value={formData.Firma} onChange={handleInputChange} />
                            <Textarea label="Nachricht" name="Nachricht" placeholder="Nachricht" value={formData.Nachricht} onChange={handleInputChange} />

                            <Button className="button_100" onClick={handleSubmit} label="Nachricht senden" />
                        </div>

                    </div>
                </div>
            </div>
        </div >
    );
}
